/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-restricted-globals */
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Toolbar,
  Tooltip,
  Typography,
  TableSortLabel,
  TableBody
} from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import React, { useState } from 'react';
import { showToast } from 'modules/layout/layout.actions';
import { useDropzone } from 'react-dropzone';
import { Delete, Download } from '@mui/icons-material';
import { postAuthorizationComment, updateAuthorizationComment } from 'modules/authorizations/store/actions/authorizations.actions';
import FilterListIcon from '@mui/icons-material/FilterList';
import { REACT_APP_INTAKE_API_V2_URL } from 'modules/authorizations/store/actions/attachments.actions';
import auth from 'auth/auth';
import SpinnerComponent from '../spinner.component';

const headCells = [

  {
    id: 'fileName',
    numeric: false,
    disablePadding: false,
    label: 'File Name',
  },
  {
    id: 'fileExtension',
    numeric: true,
    disablePadding: false,
    label: 'File Type',
  },
  {
    id: 'isWebViewable',
    numeric: true,
    disablePadding: false,
    label: 'Web Viewable',
  }
];

const AttachmentsMultiSelect = props => {
  const {
    fileAttachments,
    lineAttachments,
    saveFiles,
    isUploading,
    deleteFile,
    updateFile,
    addLineAttachment,
    removeLineAttachment,
    isReadOnly
  } = props;

  const [open, setOpen] = useState(false);
  const [temporaryAttachments, setTemporaryAttachments] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selected, setSelected] = useState([]);

  const handleClose = () => {
    setTemporaryAttachments([]);
    setOpen(false);
  };

  const onDrop = acceptedFiles => {
    setTemporaryAttachments([...temporaryAttachments, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const handleSaveFile = async () => {

    await saveFiles(temporaryAttachments);

    handleClose();
  };

  const getDownloadFileName = (response, attachment) => {
    const contentDisposition = response.headers.get('content-disposition') || '';
    const utf8FileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8FileNameMatch && utf8FileNameMatch[1]) {
      return decodeURIComponent(utf8FileNameMatch[1]);
    }

    const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (fileNameMatch && fileNameMatch[1]) {
      return fileNameMatch[1];
    }

    return attachment?.fileName || `attachment-${attachment?.attachmentId}`;
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const token = auth.getAccessToken();
      if (!token) {
        props.showToast('No authentication token found. Please sign in again.', { severity: 'error' });
        return;
      }

      const response = await fetch(`${REACT_APP_INTAKE_API_V2_URL}api/files/${attachment.attachmentId}/download`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const fileBlob = await response.blob();
      const objectUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = getDownloadFileName(response, attachment);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Attachment download failed:', error);
      props.showToast('Unable to download attachment.', { severity: 'error' });
    }
  };

  const handleRemoveAttachment = async (attachment) => {
    if (!confirm(`Are you sure you want to delete the attachment?`)) {
        return;
    }
    
    await deleteFile(attachment);
  };

  const handleWebViewable = async (isChecked, attachment) => {
    if (!confirm(`Are you sure you want to change the web viewable status of the attachment?`)) {
        return;
    }

    attachment.isWebViewable = isChecked;

    await updateFile(attachment);
  }

  const handleSetLineAttachment = async (isChecked, attachment) => {
    if (isChecked) {
      // Add line attachment
      await addLineAttachment(attachment.attachmentId);
    } else {
      // Remove line attachment
      await removeLineAttachment(attachment.attachmentId);
    }
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 && fileAttachments ? Math.max(0, (1 + page) * rowsPerPage - fileAttachments.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    }  else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  return (
    <Box>
        <Box>
          <Paper sx={{ width: '100%', mb: 2 }}>
                <Toolbar
                    sx={[
                        {
                        pl: { sm: 2 },
                        pr: { xs: 1, sm: 1 },
                        },
                    ]}
                >
                        <Typography
                            sx={{ 
                                flex: '1 1 100%',
                                fontSize: '1.3125rem',
                                fontWeight: 'bold'
                            }}
                            variant="h5"
                            id="tableTitle"
                            component="div"
                        >
                            Attachments
                        </Typography>

                        <Tooltip title="Filter list">
                            <IconButton>
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip>
                </Toolbar>
        
                <Box sx={{ paddingLeft: '16px' }}>
                    <Button color="primary" onClick={() => setOpen(true)} variant="contained" disabled={isReadOnly}>
                        Add Attachments
                    </Button>
                </Box>
        
                <TableContainer>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                
                                </TableCell>
                                {headCells.map((headCell) => (
                                    <TableCell
                                        key={headCell.id}
                                        align={'left'}
                                        padding={'normal'}
                                        //sortDirection={orderBy === headCell.id ? order : false}
                                    >
                                        <TableSortLabel
                                            //active={orderBy === headCell.id}
                                            //direction={orderBy === headCell.id ? order : 'asc'}
                                            //onClick={createSortHandler(headCell.id)}
                                        >
                                            {headCell.label}
                                            {/* {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null} */}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell padding="checkbox">
                                
                                </TableCell>
                                <TableCell padding="checkbox">
                                
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fileAttachments.map((row, index) => {
                                const isItemSelected = lineAttachments ? lineAttachments.find(la => la.attachmentId === row.attachmentId) : false;
                                const labelId = `enhanced-table-checkbox-${index}`;

                                return (
                                    <TableRow
                                        hover
                                        onClick={(event) => handleClick(event, index)}
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.attachmentId}
                                        selected={isItemSelected}
                                        sx={{ cursor: 'pointer' }}
                                        disabled={isReadOnly}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                inputProps={{
                                                    'aria-labelledby': labelId,
                                                }}
                                                onChange={(e) => handleSetLineAttachment(e.target.checked, row)}
                                                disabled={isReadOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="left">{row.fileName}</TableCell>
                                        <TableCell align="left">{row.fileExtension}</TableCell>
                                        <TableCell align="left">
                                            <Checkbox
                                                checked={row.isWebViewable}
                                                disabled={false}
                                                onChange={(e) => handleWebViewable(e.target.checked, row)}
                                            />
                                        </TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Download">
                                                <IconButton color="primary" onClick={() => handleDownloadAttachment(row)}>
                                                    <Download />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Tooltip arrow placement="right" title="Delete">
                                                <IconButton color="error" onClick={() => handleRemoveAttachment(row)} disabled={isReadOnly}>
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow
                                style={{
                                    height: (53) * emptyRows,
                                }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={fileAttachments.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>Upload Attachments</DialogTitle>
          <DialogContent>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed gray',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              <input {...getInputProps()} />
              <Typography>Drag & drop files here, or click to select files</Typography>
            </Box>
            <Box>
              {temporaryAttachments.length > 0 ? (
                temporaryAttachments.map((file, index) => <Typography key={index}>{file.name}</Typography>)
              ) : (
                <Typography>No files uploaded</Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            {isUploading && (
              <SpinnerComponent/>
            )}
            {!isUploading && (
              <Box>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  variant="text"
                  sx={{
                    color: '#868686',
                    '&:hover': {
                      color: '#474747',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleSaveFile} color="primary">
                  Save Files
                </Button>
              </Box>
            )}
          </DialogActions>
        </Dialog>
    </Box>
  );
};

const mapStateToProps = state => ({});

export default compose(connect(mapStateToProps, { postAuthorizationComment, updateAuthorizationComment, showToast }))(
  AttachmentsMultiSelect
);
